import { getRemark, getToken, getUser, getUserEmail, getUserId } from './auth'
import {
  buildQiabotFrameMessages,
  buildQiabotGuestIdentifier,
  buildQiabotLogPayload,
  buildQiabotUserInfo
} from './qiabotUser'

export const QIABOT_NEW_TYPE = 'qiabot_new'

const STORAGE_KEY = 'qiabotNewBaseUrl'
const GUEST_IDENTIFIER_KEY = 'qiabotGuestIdentifier'
const SCRIPT_ID = 'qiabot-new-sdk'
const HIDE_BUBBLE_STYLE_ID = 'qb-chat-hide-bubble'

export function isQiabotNewType(type) {
  return type === QIABOT_NEW_TYPE || type === 'qiabot-new' || type === 'qiabotNew'
}

export function normalizeQiabotBaseUrl(link) {
  const value = String(link || '').trim()
  if (!value) {
    return ''
  }

  const baseUrlMatch = value.match(/BASE_URL\s*=\s*["']([^"']+)["']/i)
  const urlMatch = value.match(/https?:\/\/[^\s"'<>]+/i)
  const candidate = (baseUrlMatch && baseUrlMatch[1]) || (urlMatch && urlMatch[0]) || value

  return candidate
    .trim()
    .replace(/[),;]+$/, '')
    .replace(/\/sdk\.js(?:\?.*)?$/i, '')
    .replace(/\/+$/, '')
}

function getQiabotLang() {
  return 'en-US'
}

function readLocalStorageValue(key) {
  try {
    return window.localStorage.getItem(key) || ''
  } catch (error) {
    return ''
  }
}

function writeLocalStorageValue(key, value) {
  try {
    window.localStorage.setItem(key, value)
  } catch (error) {
    // Cookie fallback below still gives the browser a stable guest identity.
  }
}

function readCookieValue(key) {
  if (typeof document === 'undefined') {
    return ''
  }

  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedKey}=([^;]*)`))
  if (!match) {
    return ''
  }

  try {
    return decodeURIComponent(match[1])
  } catch (error) {
    return match[1]
  }
}

function writeCookieValue(key, value) {
  if (typeof document === 'undefined') {
    return
  }

  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString()
  document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

function createRandomToken() {
  if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
    const values = new Uint32Array(4)
    window.crypto.getRandomValues(values)
    return Array.from(values).map(value => value.toString(36)).join('')
  }

  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
}

function createQiabotGuestIdentifier() {
  return `hb_v3_guest_${Date.now().toString(36)}_${createRandomToken()}`
}

function getQiabotGuestIdentifier() {
  const storedIdentifier = (
    readLocalStorageValue(GUEST_IDENTIFIER_KEY) ||
    readCookieValue(GUEST_IDENTIFIER_KEY)
  ).trim()
  const identifier = buildQiabotGuestIdentifier({
    storedIdentifier,
    createIdentifier: createQiabotGuestIdentifier
  })

  writeLocalStorageValue(GUEST_IDENTIFIER_KEY, identifier)
  writeCookieValue(GUEST_IDENTIFIER_KEY, identifier)
  return identifier
}

function ensureQiabotBubbleHidden() {
  let style = document.getElementById(HIDE_BUBBLE_STYLE_ID)
  if (!style) {
    style = document.createElement('style')
    style.id = HIDE_BUBBLE_STYLE_ID
    style.textContent = '#qb-chat-bubble{display:none!important;}'
    document.head.appendChild(style)
  }

  const bubble = document.getElementById('qb-chat-bubble')
  if (bubble) {
    bubble.style.display = 'none'
  }
}

function getCurrentQiabotUserInfo() {
  const isAuthenticated = Boolean(getToken())
  const userId = isAuthenticated ? getUserId() : ''
  return buildQiabotUserInfo({
    isAuthenticated,
    userId,
    guestIdentifier: userId ? '' : getQiabotGuestIdentifier(),
    userName: isAuthenticated ? getUser() : '',
    userEmail: isAuthenticated ? getUserEmail() : '',
    remark: isAuthenticated ? getRemark() : '',
  })
}

function getCurrentQiabotUserSignature() {
  return JSON.stringify(getCurrentQiabotUserInfo())
}

function buildQiabotConfig(baseUrl) {
  const lang = getQiabotLang()
  const config = {
    baseUrl,
    position: 'right',
    bubbleConfig: {},
  }

  if (lang) {
    config.lang = lang
  }

  return Object.assign(config, getCurrentQiabotUserInfo())
}

function logQiabotConfig(config) {
  if (typeof console !== 'undefined' && typeof console.log === 'function') {
    console.log('[qiabot_new] ChatWidget.init config', buildQiabotLogPayload(config))
  }
}

function getQiabotPageContext() {
  return {
    href: window.location && window.location.href,
    referrer: document.referrer
  }
}

function getQiabotTargetOrigin(baseUrl) {
  try {
    return new URL(baseUrl, window.location.origin).origin
  } catch (error) {
    return '*'
  }
}

function postQiabotFrameMessages(widget, autoCreateConversation) {
  if (!widget || !widget.iframe || !widget.iframe.contentWindow) {
    return false
  }

  const config = widget.config || buildQiabotConfig(window.__qiabotNewBaseUrl || '')
  const targetOrigin = getQiabotTargetOrigin(config.baseUrl)
  const messages = buildQiabotFrameMessages(config, getQiabotPageContext(), autoCreateConversation)

  try {
    messages.forEach(message => {
      widget.iframe.contentWindow.postMessage(message, targetOrigin)
    })
    return true
  } catch (error) {
    return false
  }
}

function stopQiabotFrameMessagePump(widget) {
  if (widget && widget.__qiabotNewMessagePumpLoadHandler && widget.iframe) {
    widget.iframe.removeEventListener('load', widget.__qiabotNewMessagePumpLoadHandler)
    widget.__qiabotNewMessagePumpLoadHandler = null
  }
  if (widget && widget.__qiabotNewMessagePump) {
    clearInterval(widget.__qiabotNewMessagePump)
    widget.__qiabotNewMessagePump = null
  }
  if (widget) {
    widget.__qiabotNewMessagePumpStarted = false
  }
}

export function resetQiabotWidget() {
  if (typeof window === 'undefined') {
    return
  }

  const widget = window.__qiabotNewWidget
  stopQiabotFrameMessagePump(widget)

  if (widget && typeof widget.destroy === 'function') {
    widget.destroy()
  } else if (widget && widget.iframe && widget.iframe.parentNode) {
    widget.iframe.parentNode.removeChild(widget.iframe)
  }

  const bubble = document.getElementById('qb-chat-bubble')
  if (bubble && bubble.parentNode) {
    bubble.parentNode.removeChild(bubble)
  }

  window.__qiabotNewWidget = null
  window.__qiabotNewBaseUrl = ''
  window.__qiabotNewUserSignature = ''
  window.__qiabotNewLoadingPromise = null
  window.__qiabotNewLoadingBaseUrl = ''
}

function startQiabotFrameMessagePump(widget, autoCreateConversation) {
  if (!widget || !widget.iframe) {
    return
  }

  stopQiabotFrameMessagePump(widget)

  const start = () => {
    if (widget.__qiabotNewMessagePumpStarted) {
      return
    }

    widget.__qiabotNewMessagePumpStarted = true
    widget.__qiabotNewMessagePumpLoadHandler = null

    let attempts = 0
    const send = () => {
      attempts += 1
      postQiabotFrameMessages(widget, autoCreateConversation)
      if (attempts >= 20 || !widget.iframe) {
        stopQiabotFrameMessagePump(widget)
      }
    }

    send()
    widget.__qiabotNewMessagePump = setInterval(send, 50)
  }

  if (widget.iframe.__qiabotNewLoaded) {
    start()
    return
  }

  widget.__qiabotNewMessagePumpLoadHandler = () => {
    widget.iframe.__qiabotNewLoaded = true
    start()
  }
  widget.iframe.addEventListener('load', widget.__qiabotNewMessagePumpLoadHandler, { once: true })
}

function createQiabotWidget(baseUrl) {
  if (!window.ChatWidget || typeof window.ChatWidget.init !== 'function') {
    throw new Error('Qiabot ChatWidget init is unavailable')
  }

  const userSignature = getCurrentQiabotUserSignature()
  const shouldReplaceWidget = window.__qiabotNewWidget && (
    window.__qiabotNewBaseUrl !== baseUrl || window.__qiabotNewUserSignature !== userSignature
  )

  if (shouldReplaceWidget) {
    resetQiabotWidget()
  }

  const config = buildQiabotConfig(baseUrl)
  logQiabotConfig(config)
  const widget = window.ChatWidget.init(config)
  window.__qiabotNewWidget = widget
  window.__qiabotNewBaseUrl = baseUrl
  window.__qiabotNewUserSignature = userSignature
  ensureQiabotBubbleHidden()
  return widget
}

export function initQiabotWidget(link) {
  if (typeof window === 'undefined') {
    return Promise.resolve(null)
  }

  const baseUrl = normalizeQiabotBaseUrl(link)
  if (!baseUrl) {
    return Promise.resolve(null)
  }

  window.localStorage.setItem(STORAGE_KEY, baseUrl)
  ensureQiabotBubbleHidden()

  const userSignature = getCurrentQiabotUserSignature()
  if (
    window.__qiabotNewWidget &&
    window.__qiabotNewBaseUrl === baseUrl &&
    window.__qiabotNewUserSignature === userSignature
  ) {
    return Promise.resolve(window.__qiabotNewWidget)
  }

  if (window.ChatWidget && typeof window.ChatWidget.init === 'function') {
    return Promise.resolve(createQiabotWidget(baseUrl))
  }

  if (window.__qiabotNewLoadingPromise && window.__qiabotNewLoadingBaseUrl === baseUrl) {
    return window.__qiabotNewLoadingPromise
  }

  const oldScript = document.getElementById(SCRIPT_ID)
  if (oldScript) {
    oldScript.parentNode.removeChild(oldScript)
  }

  window.__qiabotNewLoadingBaseUrl = baseUrl
  window.__qiabotNewLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `${baseUrl}/sdk.js`
    script.defer = true
    script.async = true
    script.onload = () => {
      try {
        const widget = createQiabotWidget(baseUrl)
        window.__qiabotNewLoadingPromise = null
        resolve(widget)
      } catch (error) {
        window.__qiabotNewLoadingPromise = null
        reject(error)
      }
    }
    script.onerror = () => {
      window.__qiabotNewLoadingPromise = null
      reject(new Error(`Failed to load Qiabot SDK: ${script.src}`))
    }
    document.head.appendChild(script)
  })

  return window.__qiabotNewLoadingPromise
}

function showQiabotWidget(widget) {
  if (!widget) {
    return false
  }

  ensureQiabotBubbleHidden()

  if (typeof widget.createChatWindow === 'function') {
    widget.isOpen = true
    if (widget.bubble) {
      widget.bubble.style.display = 'none'
    }
    widget.createChatWindow(true)
    startQiabotFrameMessagePump(widget, true)
    return true
  }

  const bubble = document.getElementById('qb-chat-bubble')
  if (bubble) {
    bubble.click()
    ensureQiabotBubbleHidden()
    startQiabotFrameMessagePump(widget, true)
    return true
  }

  return false
}

export function openQiabotWidget(link) {
  if (typeof window === 'undefined') {
    return Promise.resolve(false)
  }

  const baseUrl = normalizeQiabotBaseUrl(link || window.localStorage.getItem(STORAGE_KEY))
  const userSignature = getCurrentQiabotUserSignature()
  if (
    window.__qiabotNewWidget &&
    (!baseUrl || window.__qiabotNewBaseUrl === baseUrl) &&
    window.__qiabotNewUserSignature === userSignature
  ) {
    return Promise.resolve(showQiabotWidget(window.__qiabotNewWidget))
  }

  if (!baseUrl) {
    return Promise.resolve(false)
  }

  return initQiabotWidget(baseUrl).then(showQiabotWidget)
}

if (typeof window !== 'undefined' && !window.__qiabotNewAuthClearListenerRegistered) {
  window.addEventListener('auth:cleared', resetQiabotWidget)
  window.__qiabotNewAuthClearListenerRegistered = true
}
